/**
 * Central state container for the V2 transport wizard page.
 *
 * The reducer keeps a deliberately stable state shape, extended with live
 * data (instances, packages, precheckByBucket, globalRefs, orphans,
 * artifactConfigs, pipelineRun, releaseCandidate).
 *
 * Key conventions:
 *   - state.sel[sourceId][werkKey] = true   (Werk selection, keyed by source id)
 *   - state.cfg[werkKey][sourceId][paramKey] = normalizedConfigEntry
 *   - state.precheckByBucket[bucketKey] = { comparison, orphans, references, error }
 *     bucketKey = `${targetInstanceId}__${werkKennung||"std"}`
 *   - state.pipeline: idle | snapshotting | dispatching | running | done | pending | failed | released
 *   - suffixedId is NEVER reconstructed, it is always passed through from precheck.
 */

import React, { createContext, useContext, useMemo, useReducer } from "react";

const initialState = {
  step: 0,
  spaceKey: null,
  spaceId: null,
  space: null,
  sourceInstanceId: null,
  targetInstanceIds: [],
  werkOn: {},
  pkgId: null,
  pkgName: null,
  pkgVersion: null,
  pkgMod: null,
  precheckByBucket: {},
  globalRefs: [],
  refSel: {},
  artifactDeselection: {},
  globalRefDeselection: {},
  orphanSel: {},
  expanded: {},
  sel: {},
  cfg: {},
  // Parameter values maintained in Git, preloaded per plant suffix and source artifact:
  //   { "<suffix>" | "": { "<sourceArtifactId>": { "<paramKey>": { DataType, Description,
  //                                                                values: { __base__, <stage> } } } } }
  // Git is the truth for the parameters. Without this preload the wizard only showed the tenant
  // values and the snapshot wrote away everything it did not know about - a transport to QAS
  // deleted the maintained PROD values.
  gitCfg: {},
  // idle/laedt hold the configuration view, da/leer/fehler let it continue.
  // "leer" = no space or no package, so nothing to preload.
  gitCfgState: "idle",
  gitCfgUnvollstaendig: false,
  cfgTab: null,
  workingTab: "all",
  workingTabAuto: true,
  scheduleCount: null,
  pipeline: "idle",
  pipelineError: null,
  runInfo: null,
  execLogs: [],
  pdReplicate: true,
  pdSummary: { pids: 0, chained: 0 },
  // Selected Partner Directory identifiers (Pids) that get replicated along
  // with the transport. Combines auto-detected Pids (from BPMN plus
  // ProcessDirect reverse match) with manually added ones. Ignored when
  // pdReplicate is false. Always deduplicated.
  pdSelectedPids: [],
  // Per-Pid stage filter: { [pid]: string[] } with stage keys ("qas",
  // "preprod", "prod", lowercase without a hyphen, as expected by the backend
  // normalizer normStage). When a Pid has no entry, the backend treats it as
  // all stages ("*"), matching the old behavior. Reset together with
  // pdSelectedPids on package change.
  pdStagesByPid: {},
  releaseState: "idle",
  releaseCandidate: null,
  releaseError: null,
  finalized: false,
};

/**
 * Keys-only variant of vorwaertsStufen(state). The reducer must not build
 * selector objects, but needs the forward-stage keys for PD pre-selection.
 * Deliberately the same rule as vorwaertsStufen: from the target stage up to
 * PROD, only stages with a reachable instance.
 */
function vorwaertsStufenKeysAus(space) {
  if (!space || !Array.isArray(space.stages) || !space.zielStufe) return [];
  const startIdx = space.stages.findIndex((st) => st.stageKey === space.zielStufe);
  if (startIdx < 0) return [];
  const out = [];
  for (let i = startIdx; i < space.stages.length; i++) {
    const st = space.stages[i];
    if (!st || !st.instanceId || !st.stageKey) continue;
    out.push(st.stageKey);
  }
  return out;
}

/**
 * Pre-fills the default set of forward stages for every Pid in list that has
 * no entry in stagesMap yet. An existing selection (deliberately narrowed by
 * the user) is never overwritten, otherwise every re-fetched response would
 * wipe out the user's intent.
 */
function pdStagesVorbelegen(stagesMap, list, defaults) {
  if (!Array.isArray(list) || !list.length) return stagesMap;
  if (!Array.isArray(defaults) || !defaults.length) return stagesMap;
  let out = null;
  for (const pid of list) {
    if (typeof pid !== "string" || !pid) continue;
    if (Array.isArray(stagesMap?.[pid])) continue;
    if (!out) out = { ...(stagesMap || {}) };
    out[pid] = [...defaults];
  }
  return out || stagesMap || {};
}

function reducer(state, action) {
  switch (action.type) {
    case "SET_STEP":
      return { ...state, step: action.payload };
    case "PICK_SPACE": {
      const { space, sourceInstance, qasInstance, prodInstance, werkMap, spaceKey } = action.payload;
      const changed = state.spaceKey !== spaceKey;
      if (!changed) return state;
      const werkOn = {};
      const werkeList = qasInstance ? extractWerkeFromInstance(qasInstance) : [];
      for (const w of werkeList) werkOn[w.key] = true;
      return {
        ...initialState,
        scheduleCount: state.scheduleCount,
        spaceKey,
        spaceId: space?.id || null,
        space: { ...space, sourceInstance, qasInstance, prodInstance, werkeList },  // space carries stufen/quellStufe/zielStufe from Scene0
        sourceInstanceId: sourceInstance?.id || null,
        targetInstanceIds: qasInstance ? [qasInstance.id] : [],
        werkOn,
      };
    }
    case "TOGGLE_WERK": {
      const key = action.payload;
      const next = { ...state.werkOn, [key]: !state.werkOn[key] };
      // gitCfg depends on the plant selection: a newly enabled plant has its own file in Git
      // that has not been loaded yet. Keeping the old state would show no maintained values for
      // it and overwrite them on the next snapshot.
      return { ...state, werkOn: next, precheckByBucket: {}, sel: {}, cfg: {}, cfgTab: null, gitCfg: {}, gitCfgState: "idle" };
    }
    case "PICK_PACKAGE": {
      const { id, name, version, mod } = action.payload;
      const changed = state.pkgId !== id;
      if (!changed) return state;
      return {
        ...state,
        pkgId: id,
        pkgName: name || id,
        pkgVersion: version || null,
        pkgMod: mod || null,
        precheckByBucket: {},
        globalRefs: [],
        refSel: {},
        sel: {},
        cfg: {},
        gitCfg: {},
        gitCfgState: "idle",
        cfgTab: null,
        workingTab: "all",
        artifactDeselection: {},
        globalRefDeselection: {},
        orphanSel: {},
        // Detected Pids belong to a specific package. When the package
        // changes, the selection must be rebuilt, otherwise PD identifiers
        // end up attached to the wrong package.
        pdSelectedPids: [],
        // The per-Pid stage filter belongs to the package the same way: stale
        // state must not override the pre-selection in the new package.
        pdStagesByPid: {},
      };
    }
    case "SET_PRECHECK":
      return { ...state, precheckByBucket: action.payload || {} };
    case "SET_GLOBAL_REFS": {
      const list = Array.isArray(action.payload) ? action.payload : [];
      const refSel = { ...state.refSel };
      for (const r of list) {
        const id = r?.bundleId || r?.id || r?.Name || r?.name || r?.Id;
        if (!id) continue;
        // An unresolvable reference (backend: resolvable=false) must never end
        // up in the transport selection. Force the selection to false, even
        // overriding an existing pre-selection: the user could never transport
        // this reference with a meaningful result.
        if (r?.resolvable === false) {
          refSel[id] = false;
          continue;
        }
        if (refSel[id] === undefined) {
          const status = String(r?.status || "").toLowerCase();
          refSel[id] = status !== "identical";
        }
      }
      return { ...state, globalRefs: list, refSel };
    }
    case "SET_SEL":
      return { ...state, sel: action.payload || {} };
    case "TOGGLE_SEL": {
      const { sourceId, werkKey, on } = action.payload;
      const cur = { ...(state.sel[sourceId] || {}) };
      if (on) cur[werkKey] = true;
      else delete cur[werkKey];
      const next = { ...state.sel, [sourceId]: cur };
      return { ...state, sel: next };
    }
    case "TOGGLE_REF": {
      const id = action.payload;
      // An unresolvable reference (resolvable=false) must never end up in the
      // selection. The checkbox is disabled, but a programmatic dispatch
      // should also bounce off this check (defense in depth).
      const ref = (state.globalRefs || []).find((r) => (r?.bundleId || r?.id || r?.Name || r?.name || r?.Id) === id);
      if (ref?.resolvable === false) return state;
      return { ...state, refSel: { ...state.refSel, [id]: !state.refSel[id] } };
    }
    case "TOGGLE_EXPAND": {
      const id = action.payload;
      return { ...state, expanded: { ...state.expanded, [id]: !state.expanded[id] } };
    }
    case "SET_EXPANDED":
      return { ...state, expanded: action.payload || {} };
    case "SET_WORKING_TAB":
      return { ...state, workingTab: action.payload, workingTabAuto: false };
    case "SET_WORKING_TAB_AUTO":
      return { ...state, workingTab: action.payload, workingTabAuto: true };
    case "SET_CFG": {
      // stageValues (optional): { [stageKey]: [{ParameterKey, targetValue}] }.
      // Merged per entry into entries[i].byStage[stageKey]. The entry's root
      // stays the BASE value (the first forward stage), which keeps it
      // backward compatible with the single-stage view.
      const { werkKey, sourceId, cacheKey, entries, stageValues } = action.payload;
      const stufen = stageValues && typeof stageValues === "object" ? stageValues : null;
      // One ParameterKey -> targetValue lookup per stage, for fast access in
      // the map below.
      const stageLookup = {};
      if (stufen) {
        for (const [stageKey, list] of Object.entries(stufen)) {
          const m = {};
          for (const v of Array.isArray(list) ? list : []) {
            if (v && v.ParameterKey != null) m[v.ParameterKey] = v.targetValue;
          }
          stageLookup[stageKey] = m;
        }
      }
      // Values maintained in Git for this plant. They BEAT the tenant value: Git is the truth
      // for the parameters, the tenant only shows what happens to be there right now. Without
      // this precedence the user would see the tenant value again on every transport, adopt it,
      // and the state maintained in Git would be silently overwritten.
      const gitWerk = (state.gitCfg && state.gitCfg[werkKey]) || {};
      const gitArt = gitWerk[sourceId] || {};
      const enrichedEntries = (entries || []).map((e) => {
        if (!e) return e;
        const git = gitArt[e.ParameterKey];
        const gitBasis = git?.values?.__base__;
        const root = gitBasis != null && gitBasis !== ""
          ? { ...e, newValue: gitBasis, fromGit: true, fromTarget: false }
          : e;
        if (!stufen) return root;
        const byStage = { ...(root.byStage || {}) };
        for (const stageKey of Object.keys(stageLookup)) {
          const targetValue = stageLookup[stageKey][e.ParameterKey];
          const gitWert = git?.values?.[stageKey];
          const ausGit = gitWert != null && gitWert !== "";
          byStage[stageKey] = {
            targetValue: targetValue == null ? "" : targetValue,
            newValue: ausGit ? gitWert : (targetValue || ""),
            fromTarget: !ausGit && !!targetValue,
            fromGit: ausGit,
            modified: false,
          };
        }
        return { ...root, byStage };
      });
      const werkCfg = { ...(state.cfg[werkKey] || {}) };
      werkCfg[sourceId] = { cacheKey, entries: enrichedEntries };
      return { ...state, cfg: { ...state.cfg, [werkKey]: werkCfg } };
    }
    case "SET_GIT_CFG": {
      // Preloaded Git values. The key here is the UI's plant key ("std" = no plant), while the
      // backend delivers the suffix ("" = no plant) - rekeyed once here so the rest of the view
      // only ever deals with one spelling.
      const { werke, unvollstaendig } = action.payload || {};
      const gitCfg = {};
      for (const [kennung, artefakte] of Object.entries(werke || {})) {
        gitCfg[kennung === "" ? "std" : kennung] = artefakte || {};
      }
      return { ...state, gitCfg, gitCfgState: "da", gitCfgUnvollstaendig: !!unvollstaendig };
    }
    case "SET_GIT_CFG_STATE":
      return { ...state, gitCfgState: action.payload };
    case "PATCH_CFG_ENTRY": {
      // Optional payload.stage: writes into entries[i].byStage[stage],
      // otherwise into the root (base) entry.
      const { werkKey, sourceId, paramKey, patch, stage } = action.payload;
      const werk = state.cfg[werkKey] || {};
      const artef = werk[sourceId] || { entries: [] };
      const entries = (artef.entries || []).map((e) => {
        if (e.ParameterKey !== paramKey) return e;
        if (!stage) return { ...e, ...patch };
        const byStage = { ...(e.byStage || {}) };
        byStage[stage] = { ...(byStage[stage] || {}), ...patch };
        return { ...e, byStage };
      });
      const nextArtef = { ...artef, entries };
      return {
        ...state,
        cfg: {
          ...state.cfg,
          [werkKey]: { ...werk, [sourceId]: nextArtef },
        },
      };
    }
    case "SET_CFG_TAB":
      return { ...state, cfgTab: action.payload };
    case "SET_SCHEDULE_COUNT":
      return { ...state, scheduleCount: action.payload };
    case "SET_PIPELINE_STATE":
      return {
        ...state,
        pipeline: action.payload.pipeline,
        pipelineError: action.payload.error ?? state.pipelineError,
        runInfo: action.payload.runInfo ?? state.runInfo,
      };
    case "APPEND_LOG":
      return { ...state, execLogs: [...state.execLogs, action.payload] };
    case "RESET_LOGS":
      return { ...state, execLogs: [] };
    case "SET_PD_REPLICATE":
      return { ...state, pdReplicate: !!action.payload };
    case "SET_PD_SUMMARY":
      return { ...state, pdSummary: action.payload || { pids: 0, chained: 0 } };
    case "SET_PD_SELECTED_PIDS": {
      const list = Array.isArray(action.payload) ? action.payload : [];
      const dedup = Array.from(new Set(list.filter((p) => typeof p === "string" && p)));
      // Pre-fill new Pids with all forward stages; existing ones are left
      // untouched. This makes an unrestricted Pid behave exactly like today:
      // all stages.
      const defaults = vorwaertsStufenKeysAus(state.space);
      const stagesNext = pdStagesVorbelegen(state.pdStagesByPid || {}, dedup, defaults);
      // Cleanup: stage entries for deselected Pids are dropped too. Otherwise
      // deselecting and later reselecting a Pid would bring back its old
      // (possibly narrowed) filter, which the user no longer has in mind.
      const selSet = new Set(dedup);
      const cleaned = {};
      for (const [pid, stg] of Object.entries(stagesNext || {})) {
        if (selSet.has(pid)) cleaned[pid] = stg;
      }
      return { ...state, pdSelectedPids: dedup, pdStagesByPid: cleaned };
    }
    case "TOGGLE_PD_PID": {
      const pid = action.payload;
      if (!pid || typeof pid !== "string") return state;
      const cur = new Set(state.pdSelectedPids || []);
      const stagesNext = { ...(state.pdStagesByPid || {}) };
      if (cur.has(pid)) {
        cur.delete(pid);
        // Drop the filter too, so a later reselect starts with a clean
        // pre-selection.
        delete stagesNext[pid];
      } else {
        cur.add(pid);
        // Only set the pre-selection if no entry exists yet.
        if (!Array.isArray(stagesNext[pid])) {
          const defaults = vorwaertsStufenKeysAus(state.space);
          if (defaults.length) stagesNext[pid] = [...defaults];
        }
      }
      return { ...state, pdSelectedPids: Array.from(cur), pdStagesByPid: stagesNext };
    }
    case "TOGGLE_PD_STAGE": {
      // Toggle a single stage for a Pid. Deselecting the last stage removes
      // the Pid from the selection entirely, otherwise the UI would show the
      // contradictory state "Pid selected, but for no stage".
      const { pid, stage } = action.payload || {};
      if (!pid || !stage) return state;
      const cur = new Set(state.pdStagesByPid?.[pid] || []);
      if (cur.has(stage)) cur.delete(stage); else cur.add(stage);
      const naechsteStufen = Array.from(cur);
      if (naechsteStufen.length === 0) {
        // Last stage removed -> the Pid drops out. Consistent with "no stage
        // selected means transport nowhere".
        const selNext = (state.pdSelectedPids || []).filter((p) => p !== pid);
        const stagesNext = { ...(state.pdStagesByPid || {}) };
        delete stagesNext[pid];
        return { ...state, pdSelectedPids: selNext, pdStagesByPid: stagesNext };
      }
      return {
        ...state,
        pdStagesByPid: { ...(state.pdStagesByPid || {}), [pid]: naechsteStufen },
      };
    }
    case "SET_PD_STAGES": {
      // Set the complete stage set for a Pid (e.g. "select all" / "clear
      // all"). An empty list drops the Pid (see TOGGLE_PD_STAGE).
      const { pid, stages } = action.payload || {};
      if (!pid) return state;
      const bereinigt = Array.from(new Set((Array.isArray(stages) ? stages : []).filter(Boolean)));
      if (bereinigt.length === 0) {
        const selNext = (state.pdSelectedPids || []).filter((p) => p !== pid);
        const stagesNext = { ...(state.pdStagesByPid || {}) };
        delete stagesNext[pid];
        return { ...state, pdSelectedPids: selNext, pdStagesByPid: stagesNext };
      }
      return {
        ...state,
        pdStagesByPid: { ...(state.pdStagesByPid || {}), [pid]: bereinigt },
      };
    }
    case "SET_RELEASE_STATE":
      return {
        ...state,
        releaseState: action.payload.state,
        releaseError: action.payload.error ?? state.releaseError,
      };
    case "SET_RELEASE_CANDIDATE":
      return { ...state, releaseCandidate: action.payload };
    case "SET_FINALIZED":
      return { ...state, finalized: !!action.payload };
    case "RESET_ALL":
      return { ...initialState, scheduleCount: state.scheduleCount };
    default:
      return state;
  }
}

function extractWerkeFromInstance(inst) {
  if (!inst || !Array.isArray(inst.werke)) return [];
  return inst.werke
    .map((w) => {
      if (typeof w === "string") {
        const parts = w.split("__");
        if (parts.length >= 2) return { key: parts[1] || parts[0], name: parts[0], kennung: parts[1] };
        return { key: parts[0], name: parts[0], kennung: parts[0] };
      }
      const key = w?.kennung || w?.name || "";
      return { key, name: w?.name || key, kennung: w?.kennung || key };
    })
    .filter((w) => w.kennung);
}

const Ctx = createContext(null);

export function TransportV2Provider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const value = useMemo(() => ({ state, dispatch }), [state]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTransportV2() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useTransportV2 muss innerhalb TransportV2Provider gerufen werden");
  return ctx;
}

// Selectors
export function hasWerke(state) {
  return !!(state.space?.werkeList?.length);
}
export function allWerke(state) {
  return state.space?.werkeList || [];
}
export function activeWerke(state) {
  return allWerke(state).filter((w) => state.werkOn[w.key]);
}
export function buckets(state) {
  return hasWerke(state) ? activeWerke(state) : [{ key: "std", name: "Standard", kennung: "" }];
}
export function visBuckets(state) {
  const b = buckets(state);
  if (hasWerke(state) && state.workingTab !== "all") {
    return b.filter((x) => x.key === state.workingTab);
  }
  return b;
}

/**
 * Forward stages from Scene0: all stages from the target stage onward
 * (inclusive), limited to stages with a reachable instance.
 *
 * Example: space DEV/QAS/PRE-PROD/PROD with target stage QAS returns
 * [QAS, PRE-PROD, PROD]. A single-stage space (source plus target only)
 * returns [QAS], matching the old single-stage view.
 *
 * @returns {{key: string, code: string, name: string, instanceId: string}[]}
 */
export function vorwaertsStufen(state) {
  const s = state.space;
  if (!s || !Array.isArray(s.stages)) return [];
  const zielKey = s.zielStufe || null;
  if (!zielKey) return [];
  const startIdx = s.stages.findIndex((st) => st.stageKey === zielKey);
  if (startIdx < 0) return [];
  const out = [];
  for (let i = startIdx; i < s.stages.length; i++) {
    const st = s.stages[i];
    if (!st || !st.instanceId) continue;
    out.push({ key: st.stageKey, code: st.code, name: st.inst || st.name, instanceId: st.instanceId });
  }
  return out;
}

/**
 * Selector: effective PD Pids for a given target stage. When a Pid has no
 * filter, it implicitly applies to "all stages" (matching the backend
 * export), so an unrestricted Pid keeps the old behavior exactly.
 *
 * @param {string} targetStage normalized stage key ("qas"|"preprod"|"prod"|"dev")
 */
export function pdPidsFuerStufe(state, targetStage) {
  const list = Array.isArray(state.pdSelectedPids) ? state.pdSelectedPids : [];
  if (!targetStage) return list.slice();
  const map = state.pdStagesByPid || {};
  return list.filter((pid) => {
    const stg = map[pid];
    if (!Array.isArray(stg) || stg.length === 0) return true; // implicitly all stages
    return stg.includes(targetStage);
  });
}
